import React from "react";
import { IconUpload } from "./icons";

// Reusable, presentational form fields shared by both wizards.
// Each takes: label, name, value, onChange (event handler), error, required, full.

export const TextField = ({ label, name, value, onChange, error, required, full, ...rest }) => {
  const id = `crs-${name}`;
  return (
    <div className={`crs-field${full ? " crs-field--full" : ""}`}>
      <label className="crs-field__label" htmlFor={id}>
        {label}{required && <span className="crs-field__req">*</span>}
      </label>
      <input
        id={id} name={name} value={value} onChange={onChange}
        className={`crs-input${error ? " is-invalid" : ""}`} aria-invalid={!!error} {...rest}
      />
      {error && <span className="crs-field__error" role="alert">{error}</span>}
    </div>
  );
};

export const SelectField = ({ label, name, value, onChange, error, required, full, options = [], placeholder = "Select", disabled }) => {
  const id = `crs-${name}`;
  return (
    <div className={`crs-field${full ? " crs-field--full" : ""}`}>
      <label className="crs-field__label" htmlFor={id}>
        {label}{required && <span className="crs-field__req">*</span>}
      </label>
      <div className="crs-select-wrap">
        <select
          id={id} name={name} value={value} onChange={onChange} disabled={disabled}
          className={`crs-select${error ? " is-invalid" : ""}`} aria-invalid={!!error}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => {
            const v = typeof opt === "string" ? opt : opt.value;
            const l = typeof opt === "string" ? opt : opt.label;
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      </div>
      {error && <span className="crs-field__error" role="alert">{error}</span>}
    </div>
  );
};

// Searchable combobox: type to filter the suggestion list, pick one, or type a
// value that isn't listed. Native <input>+<datalist>, styled like TextField.
export const ComboField = ({ label, name, value, onChange, error, required, full, options = [], placeholder = "Type or select", disabled, ...rest }) => {
  const id = `crs-${name}`;
  const listId = `crs-list-${name}`;
  return (
    <div className={`crs-field${full ? " crs-field--full" : ""}`}>
      <label className="crs-field__label" htmlFor={id}>
        {label}{required && <span className="crs-field__req">*</span>}
      </label>
      <input
        id={id} name={name} value={value} onChange={onChange} list={listId}
        placeholder={placeholder} disabled={disabled} autoComplete="off"
        className={`crs-input${error ? " is-invalid" : ""}`} aria-invalid={!!error} {...rest}
      />
      <datalist id={listId}>
        {options.map((opt) => <option key={opt} value={opt} />)}
      </datalist>
      {error && <span className="crs-field__error" role="alert">{error}</span>}
    </div>
  );
};

export const TextareaField = ({ label, name, value, onChange, error, required, full = true, rows = 3, ...rest }) => {
  const id = `crs-${name}`;
  return (
    <div className={`crs-field${full ? " crs-field--full" : ""}`}>
      <label className="crs-field__label" htmlFor={id}>
        {label}{required && <span className="crs-field__req">*</span>}
      </label>
      <textarea
        id={id} name={name} value={value} onChange={onChange} rows={rows}
        className={`crs-textarea${error ? " is-invalid" : ""}`} aria-invalid={!!error} {...rest}
      />
      {error && <span className="crs-field__error" role="alert">{error}</span>}
    </div>
  );
};

export const FileField = ({ label, name, file, onChange, error, accept, note, required, full = true }) => {
  const id = `crs-${name}`;
  return (
    <div className={`crs-field${full ? " crs-field--full" : ""}`}>
      <label className="crs-field__label" htmlFor={id}>
        {label}{required && <span className="crs-field__req">*</span>}
      </label>
      <div className="crs-file">
        <label className={`crs-file__drop${error ? " is-invalid" : ""}`} htmlFor={id}>
          <span className="crs-file__icon"><IconUpload /></span>
          <span className="crs-file__text">
            {file ? <b>{file.name}</b> : <>Click to upload your resume <b>(PDF)</b></>}
          </span>
          <input id={id} name={name} type="file" accept={accept} onChange={onChange} />
        </label>
        {note && <p className="crs-file__note">{note}</p>}
      </div>
      {error && <span className="crs-field__error" role="alert">{error}</span>}
    </div>
  );
};
